import parser from "ua-parser-js";
import * as _ from "lodash-es";
import Fingerprint2 from "fingerprintjs2";
import excmd5 from "md5";
import * as request from "@/api/request";
import type {
  PlatformType,
  DeviceInfo,
  DevicePlatform,
  SdkParams,
  UrlParamObj,
  LinkConfigMap,
  StoreLinkParams,
  FingerprintData,
  UpdateLinkParamsOptions,
} from "./types";

export const md5 = excmd5;

/**
 * 获取URL参数
 * @param name 参数名
 * @returns 参数值
 */
export const getUrlParams = (name: string): string => {
  // 获取完整的URL
  const url = window.location.href;

  // 检查参数是否在hash前面
  const searchParams = window.location.search.substr(1);
  if (searchParams) {
    let reg = new RegExp("(^|&)" + name + "=([^&]*)(&|$)");
    let r = searchParams.match(reg);
    if (r != null) return decodeURIComponent(r[2]);
  }

  // 检查参数是否在hash后面
  const hashIndex = url.indexOf("#");
  if (hashIndex !== -1) {
    const hashPart = url.substring(hashIndex + 1);
    const questionIndex = hashPart.indexOf("?");
    if (questionIndex !== -1) {
      const hashParams = hashPart.substring(questionIndex + 1);
      let reg = new RegExp("(^|&)" + name + "=([^&]*)(&|$)");
      let r = hashParams.match(reg);
      if (r != null) return decodeURIComponent(r[2]);
    }
  }

  return "";
};

/**
 * 获取游戏ID
 * @returns 游戏ID
 */
export const getGameId = (): string => {
  const urlGameId =
    getUrlParams("gameId") ||
    getUrlParams("gameid") ||
    getUrlParams("game_id") ||
    getUrlParams("game_ID") ||
    getUrlParams("gameID");
  if (urlGameId) {
    return urlGameId;
  }

  // 根据主域名判断默认值
  const mainHost = getMainHost();
  if (mainHost === "gnjoy.hk") {
    return "386";
  }
  if (mainHost === 'globallap.com') {
    return "440";
  }

  return "191";
};

export const getUrlParamsObj = (): Record<string, string> => {
  const url = window.location.href;
  const result: Record<string, string> = {};

  // 处理 hash 前面的参数
  const searchParams = window.location.search.substr(1);
  if (searchParams) {
    searchParams.split("&").forEach((param) => {
      if (param) {
        const [key, value] = param.split("=");
        if (key) {
          result[key] = decodeURIComponent(value || "");
        }
      }
    });
  }

  // 处理 hash 后面的参数
  const hashIndex = url.indexOf("#");
  if (hashIndex !== -1) {
    const hashPart = url.substring(hashIndex + 1);
    const questionIndex = hashPart.indexOf("?");
    if (questionIndex !== -1) {
      const hashParams = hashPart.substring(questionIndex + 1);
      hashParams.split("&").forEach((param) => {
        if (param) {
          const [key, value] = param.split("=");
          if (key) {
            result[key] = decodeURIComponent(value || "");
          }
        }
      });
    }
  }

  return result;
};

/**
 * 获取主域名
 * @returns 主域名
 */
export const getMainHost = (): string => {
  if (window.location.hostname == "localhost") {
    return "37games.com";
  }
  let hostArr = window.location.hostname.split(".");
  let mainArr = hostArr.slice(-2);
  return mainArr.join(".");
};

/**
 * 获取当前年份
 * @returns 当前年份字符串
 */
export const getCurYear = (): string => {
  return `${new Date().getFullYear()}`;
};

/**
 * 调用SDK方法
 * @param action 方法名
 * @param params 参数
 */
export const sdk = (action: string, params?: SdkParams): void => {
  if (!action) return;
  try {
    if (params !== void 0) {
      window.sdkCtrl?.[action](params);
    } else {
      window.sdkCtrl?.[action]();
    }
  } catch (error) {
    try {
      window.webkit?.messageHandlers?.callSDKMethodWithParameters?.postMessage({
        method: action,
        params,
      });
    } catch (_error) { }
  }
};

/**
 * 延时函数
 * @param second 秒数
 * @returns Promise
 */
export const sleep = async (second: number): Promise<void> => {
  return new Promise((resolve) => {
    setTimeout(resolve, second * 1000);
  });
};

/**
 * 判断是否为移动设备类型
 * @returns 是否为移动设备
 */
export const isMobileType = (): boolean => {
  let info = parser();
  if (info) {
    if (info.device.type == undefined) return false;
    if (info.device.type == "tablet" || info.device.type == "smarttv") {
      return window.innerHeight > window.innerWidth;
    }
  }
  return true;
};

/**
 * 获取设备信息
 * @returns 设备信息
 */
export const getDeviceInfo = (): DeviceInfo => {
  return parser();
};

/**
 * 获取URL参数对象
 * @param ignoreKeyArr 忽略的键数组
 * @returns URL参数对象
 */
export const getUrlParamObj = (ignoreKeyArr?: string[]): UrlParamObj => {
  let search = window.location.search.substr(1);
  let res: UrlParamObj = {};
  let keyArr = search.split("&");
  for (let i in keyArr) {
    let arr = keyArr[i].split("=");
    if (ignoreKeyArr?.length && _.isArray(ignoreKeyArr)) {
      let _i = ignoreKeyArr.findIndex((item) => item == arr[0]);
      if (~_i) continue;
    }
    res[arr[0]] = arr[1];
  }

  return res;
};

/**
 * 获取系统语言
 * @returns 系统语言代码
 */
export const getSystemLanguage = function (): string {
  let res =
    navigator.language ||
    navigator.userLanguage ||
    (navigator as any).browerLanguage ||
    (navigator as any).systemLanguage;
  if (res.indexOf("-") > -1) {
    const langArr = res.split("-");
    res = langArr[0] === "zh" ? langArr[1] : langArr[0];
  }
  return res.toLowerCase();
};

/**
 * 获取设备语言
 * @returns 设备语言代码
 */
export const getDeviceLanguage = (): string => {
  return (
    getUrlParams("appLanguage") ||
    getUrlParams("language") ||
    getSystemLanguage()
  );
};

/**
 * 创建指纹
 * @param data 指纹数据
 * @returns Promise<string>
 */
const createFingerPrint = async (
  data: FingerprintData = {}
): Promise<string> => {
  return new Promise((resolve) => {
    Fingerprint2.get((components) => {
      // 参数只有回调函数时，默认浏览器指纹依据所有配置信息进行生成
      let values = components.map((component) => component.value); // 配置的值的数组
      for (let i in data) {
        values.push(data[i]);
      }
      const murmur = Fingerprint2.x64hash128(values.join(""), 31); // 生成浏览器指纹
      resolve(murmur);
    });
  });
};

/**
 * 获取指纹
 * @param data 指纹数据
 * @returns Promise<string>
 */
export const getFingerPrint = async (
  data: FingerprintData = {}
): Promise<string> => {
  let local = "";
  try {
    local = window.localStorage.getItem("finger_id_2") || "";
  } catch (error) { }
  if (!local) {
    if (!Object.keys(data).length) {
      let res = await request.getJSON("//gstore.endomainname.com/test_ip");
      if (res.result == 1) {
        data = res.data;
      }
    }
    local = await createFingerPrint(data);
    try {
      window.localStorage.setItem("finger_id_2", local);
    } catch (error) { }
  }
  return local;
};

/**
 * 旧版通用的判断设备逻辑
 * @returns 设备平台对象
 */
export const getDeviceOld = (): DevicePlatform => {
  let userAgent = window.navigator.userAgent;
  let ua = userAgent.toLowerCase();
  let platform: DevicePlatform = {
    isAndroid: function () {
      return ua.indexOf("android") > -1;
    },
    isIOS: function () {
      let NPlatform = navigator.platform;
      return (
        ua.indexOf("iphone") > -1 ||
        /iPad|iPhone|iPod/gi.test(NPlatform) ||
        (NPlatform === "MacIntel" &&
          navigator.maxTouchPoints > 1 &&
          !(window as any).MSStream)
      );
    },
    isWinPhone: function () {
      return !!navigator.userAgent.match(/Windows Phone/i);
    },
    isIpad: function () {
      return ua.indexOf("ipad") > -1;
    },
    isPhone: function () {
      return (
        this.isAndroid() || this.isIOS() || this.isWinPhone() || this.isIpad()
      );
    },
    isElectron: function () {
      return ua.indexOf("electron") > -1;
    },
  };
  return platform;
};

/**
 * 获取系统类型
 * @returns 系统类型
 */
export const systemType = (): PlatformType => {
  let _p = getDeviceOld();
  if (_p.isIOS() || _p.isIpad()) return "ios";
  if (_p.isAndroid() || _p.isWinPhone()) return "android";
  return "pc";
};

/**
 * 检测是否为横屏
 * 优先用 Screen Orientation API，避免仅用视口宽高时在输入法弹出、地址栏伸缩等场景下误判。
 * @returns 是否为横屏
 */
export const isLandscape = (): boolean => {
  const type = typeof screen !== "undefined" ? screen.orientation?.type : undefined;
  if (type) {
    return type.startsWith("landscape");
  }
  return window.innerWidth > window.innerHeight;
};

/**
 * 检测是否为移动端且横屏
 * @returns 是否为移动端横屏
 */
export const isMobileLandscape = (): boolean => {
  const device = getDeviceOld();
  return device.isPhone() && isLandscape();
};

/**
 * 获取GAPI
 * @param shost 子域名
 * @returns GAPI地址
 */
export const getGapi = (shost: string): string => {
  return `${getMainHost() == "gm99.com" ? "" : "g"}${shost}.${getMainHost()}`;
};

/**
 * 设置Cookie
 * @param name Cookie名
 * @param value Cookie值
 * @param days 过期天数
 * @param safe 是否安全
 */
export const setCookie = (
  name: string,
  value: string,
  days: number,
  safe?: boolean
): void => {
  let date = new Date();
  date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
  let expires = "expires=" + date.toGMTString();
  let safeTxt = "Secure; SameSite=None;";
  if (safe) {
    safeTxt = "";
  }
  document.cookie = `${name}=${value};${expires};path=/;${safeTxt}domain=.${getMainHost()}`;
};

/**
 * 获取Cookie
 * @param name Cookie名
 * @returns Cookie值
 */
export const getCookie = (name: string): string => {
  let reg = new RegExp("(^| )" + name + "=([^;]*)(;|$)");
  let arr = document.cookie.match(reg);
  if (arr) return arr[2];
  return "";
};

/**
 * 生成随机数
 * @param min 最小值
 * @param max 最大值
 * @returns 随机数
 */
export const randomNumber = (min: number, max: number): number => {
  const randomBuffer = new Uint32Array(1);
  window.crypto.getRandomValues(randomBuffer);
  const number = randomBuffer[0] / (0xffffffff + 1);
  return Math.floor(number * (max - min) + min);
};

/**
 * 更新链接参数
 * @param link 链接
 * @param obj 参数对象
 * @param filterRepeat 是否过滤重复参数
 * @returns 更新后的链接
 */
export const updateLinkParams = (
  link: string,
  obj: Record<string, any>,
  filterRepeat: boolean = false
): string => {
  try {
    let url = new URL(link);
    for (let k in obj) {
      if (filterRepeat && url.searchParams.has(k)) {
        continue;
      }
      url.searchParams.set(k, String(obj[k]));
    }
    let commonParams = window.globalGetLinkParams?.() || {};
    Object.keys(commonParams).forEach((key) => {
      url.searchParams.set(key, String(commonParams[key]));
    });
    return url.href;
  } catch (error) {
    return String(link);
  }
};

/**
 * 获取带有cid、scid的储值链接
 * @param params 参数对象
 * @returns 储值链接
 */
export const getStoreLinkWithCidScid = ({
  link,
  paramsObj,
  pageId,
}: StoreLinkParams): string => {
  if (!link) return "";
  let res = updateLinkParams(link, {});
  const cid = getUrlParams("cid");
  const scid = getUrlParams("scid");
  let storeLinkCid = "web";
  let storeLinkScid = `activity_${pageId}`;
  if (cid) {
    storeLinkCid = cid;
  }
  if (scid) {
    storeLinkScid = `${scid}_activity_${pageId}`;
  }
  res = updateLinkParams(res, {
    cid: storeLinkCid,
    scid: storeLinkScid,
    ...paramsObj,
  });
  return res;
};

// 活动及储值页的常用域名，用于替换链接中的域名
export const mainLinkArr = [
  "pay",
  "gabres",
  "gevents",
  "events",
  "jpevents",
  "auto-page",
  "auto-page-preview",
  "serverless",
  "gpassport",
  "gsupport",
  "service",
  "h5",
];

/**
 * 检查是否为主要链接
 * @param link 链接
 * @returns 是否为主要链接
 */
export const checkMainLink = (link: string): boolean => {
  try {
    let url = new URL(link);
    let host = url.hostname;
    let hostArr = host.split(".");
    if (hostArr.length !== 3) return false;
    let linkHost = hostArr[0];
    return mainLinkArr.includes(linkHost);
  } catch (error) {
    return false;
  }
};

/**
 * 获取最终跳转的链接
 * @param link 原始链接
 * @returns 最终链接
 */
export const getTargetLink = (link: string): string => {
  if (checkMainLink(link)) {
    // 替换link中的mainHost为当前域名中的mainHost
    try {
      let url = new URL(link);
      let host = url.hostname;
      let hostArr = host.split(".");
      let curHostArr = window.location.hostname.split(".");
      curHostArr[0] = hostArr[0];
      url.hostname = curHostArr.join(".");
      return url.href;
    } catch (error) {
      return link;
    }
  }
  return link;
};

// 这些都是新旧版本的兼容代码
export const getUrlParms = getUrlParams;

const link: LinkConfigMap = {
  gm99: {
    activity: "https://eventsapi.gm99.com/activity/execute",
    getUserJson: "https://passport.gm99.com/ajax/get_user_json",
    logout: "https://passport.gm99.com/login/logout2",
    loginUrl: `https://passport.gm99.com/?device=${systemType() == "pc" ? 1 : 2
      }`,
  },
  jp: {
    activity: "https://jpeventsapi.37games.com/activity/execute",
    getUserJson: `https://jppassport.37games.com/center/ajax/get_user_json`,
    logout: `https://jppassport.37games.com/center/ajax/logout`,
    loginUrl: "https://gabres.37games.com/platform/jpLogin.html",
  },
  global: {
    activity: `https://gactivityapi.${getMainHost()}/activity/execute`,
    getUserJson: `https://gpassport.${getMainHost()}/center/ajax/get_user_json`,
    logout: `https://gpassport.${getMainHost()}/web_login/logout`,
    loginUrl: `https://gabres.${getMainHost()}/platform/login.html`,
    privacy: `https://gpassport.${getMainHost()}/center/ServicePrivacy/privacy`,
    service: `https://gpassport.${getMainHost()}/center/ServicePrivacy/service`,
  },
};

/**
 * 获取活动接口
 * @param platform 平台
 * @param name 名称
 * @param action 动作
 * @returns 活动接口地址
 */
export const getActivityApi = (
  platform: keyof LinkConfigMap,
  name: string,
  action: string
): string => {
  return updateLinkParams(link[platform]?.activity || link.global?.activity, {
    name,
    action,
  });
};

/**
 * 获取登录态接口
 * @param platform 平台
 * @returns 登录态接口地址
 */
export const getUserJsonApi = (platform: keyof LinkConfigMap): string => {
  return link[platform]?.getUserJson || link.global?.getUserJson;
};

/**
 * 获取登出接口
 * @param platform 平台
 * @returns 登出接口地址
 */
export const getLogoutApi = (platform: keyof LinkConfigMap): string => {
  return link[platform]?.logout || link.global?.logout;
};

/**
 * 获取登陆页链接
 * @param platform 平台
 * @param gameId 游戏ID
 * @param language 语言
 * @returns 登录页链接
 */
export const getLoginUrlApi = (
  platform: keyof LinkConfigMap,
  gameId: string,
  language: string = ""
): string => {
  return updateLinkParams(link[platform]?.loginUrl || link.global?.loginUrl, {
    gameId,
    caller_redirect_url:
      window.globalGetGotoLink?.({ link: window.location.href }) ||
      window.location.href,
    language,
  });
};

/**
 * 获取隐私政策链接
 * @param platform 平台
 * @returns 隐私政策链接
 */
export const getPrivacyUrlApi = (
  platform: keyof LinkConfigMap = "global"
): string => {
  return link[platform]?.privacy || link.global?.privacy || "";
};

/**
 * 获取服务条款链接
 * @param platform 平台
 * @returns 服务条款链接
 */
export const getServiceUrlApi = (
  platform: keyof LinkConfigMap = "global"
): string => {
  return link[platform]?.service || link.global?.service || "";
};

/**
 * 格式化论坛时间戳（秒为单位），返回友好时间字符串
 * @param timestamp 秒级时间戳
 * @param t 文案函数（如 vue-i18n 的 t），需具有 time.minutesAgo/hoursAgo 翻译
 * @returns 友好时间字符串
 */
export const formatDateline = (
  timestamp: number,
  t: (key: string, values?: Record<string, any>) => string
): string => {
  const timestampMs = timestamp * 1000;
  const now = Date.now();
  const diff = now - timestampMs;
  const oneHour = 60 * 60 * 1000;
  const oneDay = 24 * oneHour;
  const oneYear = 365 * oneDay;

  if (diff < oneHour) {
    // 1小时内：xx分钟前
    const minutes = Math.floor(diff / (60 * 1000)) || 1;
    return t("time.minutesAgo", { minutes });
  } else if (diff < oneDay) {
    // 1小时到24小时内：xx小时前
    const hours = Math.floor(diff / oneHour);
    return t("time.hoursAgo", { hours });
  } else if (diff < oneYear) {
    // 24小时至一年内：展示具体日期如：05-29
    const date = new Date(timestampMs);
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${month}-${day}`;
  } else {
    // 一年外：展示年份+日期：2024-5-29
    const date = new Date(timestampMs);
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    const day = date.getDate();
    return `${year}-${month}-${day}`;
  }
};

// 解码HTML
export const decodeHtml = (html: string): string => {
  if (!html) return "";
  const txt = document.createElement("textarea");
  txt.innerHTML = html;
  return txt.value.replace(/\\r\\n/g, "\n");
};

/**
 * 从HTML内容中提取图片链接
 * @param html HTML内容
 * @returns 图片链接数组
 */
export const extractImageLinks = (html: string): string[] => {
  if (!html) return [];
  const div = document.createElement("div");
  div.innerHTML = html;

  const images = div.querySelectorAll("img");
  const imageLinks: string[] = [];
  images.forEach((img) => {
    const src = img.getAttribute("src");
    if (src && src.trim()) {
      imageLinks.push(src.trim());
    }
  });

  return imageLinks;
};

/**
 * 从URL中移除指定参数，不刷新页面
 * @param params 要移除的参数名数组
 * @returns 移除参数后的URL
 */
export const removeUrlParams = (params: string[]): string => {
  if (!params.length) return window.location.href;

  const currentUrl = window.location.href;
  const [baseUrl, hash = ""] = currentUrl.split("#");

  // 处理hash前的参数
  let beforeHashUrl = baseUrl;
  const baseQueryIndex = baseUrl.indexOf("?");
  if (baseQueryIndex !== -1) {
    const queryString = baseUrl.substring(baseQueryIndex + 1);
    const urlParams = new URLSearchParams(queryString);
    params.forEach((param) => urlParams.delete(param));

    const newQueryString = urlParams.toString();
    beforeHashUrl = newQueryString
      ? `${baseUrl.substring(0, baseQueryIndex)}?${newQueryString}`
      : baseUrl.substring(0, baseQueryIndex);
  }

  // 处理hash后的参数
  let afterHashUrl = "";
  if (hash) {
    const [hashPath, hashSearch = ""] = hash.split("?");
    if (hashSearch) {
      const urlParams = new URLSearchParams(hashSearch);
      params.forEach((param) => urlParams.delete(param));

      const newHashSearch = urlParams.toString();
      afterHashUrl = newHashSearch
        ? `#${hashPath}?${newHashSearch}`
        : `#${hashPath}`;
    } else {
      afterHashUrl = `#${hash}`;
    }
  }

  const newUrl = `${beforeHashUrl}${afterHashUrl}`;

  // 更新URL，不刷新页面
  if (newUrl !== currentUrl) {
    window.history.replaceState({}, "", newUrl);
  }

  return newUrl;
};

/**
 * 从URL中移除token参数的快捷方法
 * @returns 移除token后的URL
 */
export const removeUrlToken = (): string => {
  return removeUrlParams(["token"]);
};

/**
 * 获取语言缓存的 key
 * @param gameId 游戏ID
 * @returns localStorage key
 */
const getLanguageStorageKey = (gameId: string): string => {
  return `forum_language_${gameId}`;
};

/**
 * 根据 gameId 保存语言到 localStorage
 * @param gameId 游戏ID
 * @param language 语言代码
 */
export const saveLanguageToStorage = (
  gameId: string,
  language: string
): void => {
  try {
    const key = getLanguageStorageKey(gameId);
    window.localStorage.setItem(key, language);
  } catch (error) {
    console.error("Failed to save language to localStorage:", error);
  }
};

/**
 * 根据 gameId 从 localStorage 读取语言
 * @param gameId 游戏ID
 * @returns 语言代码，如果不存在则返回 null
 */
export const getLanguageFromStorage = (gameId: string): string | null => {
  try {
    const key = getLanguageStorageKey(gameId);
    return window.localStorage.getItem(key);
  } catch (error) {
    console.error("Failed to get language from localStorage:", error);
    return null;
  }
};

/**
 * 验证链接有效性
 * @param url 待验证的链接
 * @returns 是否为有效的安全链接
 */
export const isValidUrl = (url: string): boolean => {
  if (!url || typeof url !== "string") {
    return false;
  }

  // 去除首尾空格
  url = url.trim();

  // 检查是否为空
  if (!url) {
    return false;
  }

  try {
    // 使用 URL 构造函数验证 URL 格式
    const urlObj = new URL(url);

    // 只允许 http 和 https 协议
    const allowedProtocols = ["http:", "https:"];
    if (!allowedProtocols.includes(urlObj.protocol)) {
      return false;
    }

    // 检查是否有有效的 hostname
    if (!urlObj.hostname || urlObj.hostname.length === 0) {
      return false;
    }

    return true;
  } catch (error) {
    // URL 格式无效
    return false;
  }
};

// 首字母大写方法
export const capitalizeFirstLetter = (str: string | undefined | null): string => {
  if (!str || typeof str !== "string") {
    return "";
  }
  return str.charAt(0).toUpperCase() + str.slice(1);
};